class MedistikAnnouncementBar extends HTMLElement {
  #currentIndex = 0;
  #intervalId;
  #slides = [];
  #previousButton;
  #nextButton;

  #handlePrevious = () => {
    this.#show(this.#currentIndex - 1);
    this.#restartAutoplay();
  };

  #handleNext = () => {
    this.#show(this.#currentIndex + 1);
    this.#restartAutoplay();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stopAutoplay();
    } else {
      this.#startAutoplay();
    }
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const selectedSlide = event.target.closest('[data-announcement-slide]');
    const selectedIndex = this.#slides.indexOf(selectedSlide);

    if (selectedIndex >= 0) {
      this.#show(selectedIndex);
      this.#stopAutoplay();
    }
  };

  #handleBlockDeselect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    this.#startAutoplay();
  };

  #handleFocusOut = () => {
    window.setTimeout(this.#startAutoplay, 0);
  };

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    this.#previousButton = this.querySelector('[data-announcement-previous]');
    this.#nextButton = this.querySelector('[data-announcement-next]');

    this.#show(0, false);

    if (this.#slides.length < 2) return;

    this.#previousButton?.addEventListener('click', this.#handlePrevious);
    this.#nextButton?.addEventListener('click', this.#handleNext);
    this.addEventListener('mouseenter', this.#stopAutoplay);
    this.addEventListener('mouseleave', this.#startAutoplay);
    this.addEventListener('focusin', this.#stopAutoplay);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);

    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#previousButton?.removeEventListener('click', this.#handlePrevious);
    this.#nextButton?.removeEventListener('click', this.#handleNext);
    this.removeEventListener('mouseenter', this.#stopAutoplay);
    this.removeEventListener('mouseleave', this.#startAutoplay);
    this.removeEventListener('focusin', this.#stopAutoplay);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    this.#stopAutoplay();
  }

  #show(index, announce = true) {
    if (this.#slides.length === 0) return;

    this.#currentIndex = (index + this.#slides.length) % this.#slides.length;

    this.#slides.forEach((slide, slideIndex) => {
      const isCurrent = slideIndex === this.#currentIndex;
      slide.setAttribute('aria-hidden', String(!isCurrent));
      slide.toggleAttribute('inert', !isCurrent);
    });

    if (announce && this.hasAttribute('aria-live')) {
      this.setAttribute('aria-live', 'polite');
    }
  }

  #startAutoplay = () => {
    if (
      this.#intervalId ||
      this.#slides.length < 2 ||
      document.hidden ||
      this.matches(':hover') ||
      this.matches(':focus-within') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const seconds = Number.parseInt(this.getAttribute('autoplay') || '', 10);
    if (!Number.isFinite(seconds) || seconds < 1) return;

    this.#intervalId = window.setInterval(() => {
      this.#show(this.#currentIndex + 1);
    }, seconds * 1000);
  };

  #stopAutoplay = () => {
    if (!this.#intervalId) return;
    window.clearInterval(this.#intervalId);
    this.#intervalId = undefined;
  };

  #restartAutoplay() {
    this.#stopAutoplay();
    this.#startAutoplay();
  }
}

if (!customElements.get('medistik-announcement-bar')) {
  customElements.define('medistik-announcement-bar', MedistikAnnouncementBar);
}
